<template>
  <div class="sla-management-page">
    <div class="header-container">
      <p class="custom-title">
        Management - SLA
      </p>
      <p class="custom-header-text">
        Updated as of {{ updatedLabel }}
      </p>
    </div>

    <div
      v-if="loading && !report"
      class="text-center py-5"
    >
      Loading…
    </div>

    <div
      v-else-if="error && !report"
      class="alert alert-danger"
    >
      {{ error }}
    </div>

    <template v-else-if="report">
      <div
        v-if="loading"
        class="text-muted small px-3 pb-2"
      >
        Updating…
      </div>
      <div
        v-if="error"
        class="alert alert-warning"
      >
        {{ error }}
      </div>
      <SlaFilters
        v-model="filters"
        :brands="report.brands || []"
        :locations="report.locations || []"
        :minEndDate="report.minEndDate"
        :maxEndDate="report.maxEndDate"
        @info="showInfo = true"
      />

      <div
        v-if="!hasFilteredData && !loading"
        class="alert alert-warning"
      >
        There is no data for the date range and filters you selected.
        Please try again with a more recent time frame and updated filters.
      </div>

      <template v-else>
        <SlaMetrics
          class="mb-3"
          :callouts="callouts"
          :metric="filters.metric"
        />

        <SlaHeatmap
          :key="`ecom-${gridKey}`"
          title="SLA & SLA Pending Fulfilment Performance"
          flag="FULL"
          :rowData="ecomModel.rowData"
          :periods="ecomModel.periods"
          :pinnedBottom="ecomModel.pinnedBottom"
          :metric="filters.metric"
          :periodicity="appliedPeriodicity"
          @cell-open-notes="openNotes"
        />

        <SlaHeatmap
          :key="`ltm-${gridKey}`"
          title="SLA & SLA Pending LTM Performance"
          flag="LTM"
          :rowData="ltmModel.rowData"
          :periods="ltmModel.periods"
          :pinnedBottom="ltmModel.pinnedBottom"
          :metric="filters.metric"
          :periodicity="appliedPeriodicity"
          @cell-open-notes="openNotes"
        />
      </template>
    </template>

    <ReportOverlayDialog
      :open="showInfo"
      title="Information"
      size="lg"
      @close="showInfo = false"
    >
      <p><strong>V 4</strong></p>
      <p>
        The SLA performance shown in this dashboard is dependent on actual regular and cancelled orders
        being within the brands' accepted lower and upper forecast threshold for Units.
      </p>
      <p><strong>Definitions</strong></p>
      <p>
        <strong><em>Order Date (Start)/(End):</em></strong>
        The Customer Order Received date or the CREATED_DATE in Deposco.
        It refers to the date we receive a customer's order.
      </p>
      <p>
        <strong><em>% in SLA Status:</em></strong>
        The percent of units completed on or before the SLA Due date and time.
        This will also include automatically all orders that were above upper forecast threshold for the Brand.
      </p>
      <p><strong>Chart</strong></p>
      <p>
        The chart shows, for every Brand, the Ecom and LTM units (and percent) in SLA and SLA Pending.
      </p>
    </ReportOverlayDialog>

    <SlaNotesDialog
      v-if="notesTarget"
      :open="!!notesTarget"
      :periodicity="filters.periodicity"
      :baseDate="filters.baseDate"
      :flag="notesTarget.flag"
      :brandName="notesTarget.brandName"
      :periodKey="notesTarget.periodKey"
      @close="notesTarget = null"
      @saved="onNoteSaved"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import SlaFilters from './SlaFilters.vue';
import SlaMetrics from './SlaMetrics.vue';
import SlaHeatmap from './SlaHeatmap.vue';
import SlaNotesDialog from './SlaNotesDialog.vue';
import ReportOverlayDialog from '@/components/Reports/ReportOverlayDialog.vue';
import {
  aggregateMetricCallouts,
  buildHeatmapModel,
  filterCells,
  filterMetricSlices,
  formatUpdatedAt,
} from './heatmapUtils';
import './slaManagement.css';

function snapToSaturdayIso(iso) {
  if (!iso) return '';
  const s = String(iso).slice(0, 10);
  const [ y, m, d ] = s.split('-').map(Number);
  if (!y || !m || !d) return s;
  const date = new Date(y, m - 1, d);
  const day = date.getDay();
  if (day !== 6) {
    date.setDate(date.getDate() - ((day + 1) % 7));
  }
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export default {
  name: 'SlaManagement',
  components: {
    SlaFilters, SlaMetrics, SlaHeatmap, SlaNotesDialog, ReportOverlayDialog,
  },
  data() {
    return {
      showInfo: false,
      notesTarget: null,
      notesVersion: 0,
      filters: {
        brands: [],
        locations: [],
        endDate: '',
        baseDate: 'Created Date',
        periodicity: 'Week',
        metric: '% in SLA',
        slaType: 'Laser',
      },
      fetchKey: '',
      notesFetchKey: '',
      appliedBaseDate: 'Created Date',
      appliedPeriodicity: 'Week',
    };
  },
  computed: {
    ...mapGetters({
      report: 'slaManagement/report',
      loading: 'slaManagement/loading',
      error: 'slaManagement/error',
      noteSummaries: 'slaManagement/noteSummaries',
    }),
    updatedLabel() {
      return formatUpdatedAt(this.report?.updatedAt);
    },
    filterArgs() {
      return {
        brands: this.filters.brands,
        locations: this.filters.locations,
        baseDate: this.appliedBaseDate,
        slaType: this.filters.slaType,
      };
    },
    filteredEcom() {
      return filterCells(this.report?.ecomCells, this.filterArgs);
    },
    filteredLtm() {
      return filterCells(this.report?.ltmCells, this.filterArgs);
    },
    filteredMetrics() {
      return filterMetricSlices(this.report?.metricSlices, this.filterArgs);
    },
    hasFilteredData() {
      return this.filteredEcom.length > 0 || this.filteredLtm.length > 0;
    },
    callouts() {
      return aggregateMetricCallouts(this.filteredMetrics);
    },
    ecomModel() {
      return buildHeatmapModel(
        this.filteredEcom,
        this.filters.metric,
        this.appliedPeriodicity,
        'ECOM',
        this.filters.slaType,
        (this.noteSummaries && this.noteSummaries.FULL) || [],
      );
    },
    ltmModel() {
      return buildHeatmapModel(
        this.filteredLtm,
        this.filters.metric,
        this.appliedPeriodicity,
        'LTM',
        this.filters.slaType,
        (this.noteSummaries && this.noteSummaries.LTM) || [],
      );
    },
    gridKey() {
      return [
        this.appliedBaseDate,
        this.filters.metric,
        this.filters.slaType,
        this.appliedPeriodicity,
        (this.filters.brands || []).join('|'),
        (this.filters.locations || []).join('|'),
        this.filteredEcom.length,
        this.filteredLtm.length,
        this.notesVersion,
      ].join('::');
    },
  },
  watch: {
    'filters.periodicity'() {
      this.reloadIfNeeded();
    },
    'filters.endDate'(val, oldVal) {
      // Skip the initial sync when endDate is filled from the first response.
      if (!oldVal || !val || val === oldVal) return;
      this.reloadIfNeeded();
    },
    'filters.baseDate'() {
      this.reloadIfNeeded(true);
    },
    report() {
      if (!this.report) return;
      this.appliedBaseDate = this.filters.baseDate;
      this.appliedPeriodicity = this.filters.periodicity;
    },
  },
  async mounted() {
    if (this.filters.metric === '% in SLA with Over-forecast') {
      this.filters.metric = '% in SLA';
    }
    await this.reloadIfNeeded(true);
  },
  methods: {
    ...mapActions({
      fetchReport: 'slaManagement/fetchReport',
      fetchNoteSummaries: 'slaManagement/fetchNoteSummaries',
    }),
    openNotes(payload) {
      this.notesTarget = payload;
    },
    async onNoteSaved() {
      await this.reloadNotesIfNeeded(true);
      this.notesVersion += 1;
    },
    async reloadNotesIfNeeded(force = false) {
      if (!this.filters.periodicity || !this.filters.baseDate) return;
      const key = `${this.filters.periodicity}|${this.filters.baseDate}`;
      if (!force && key === this.notesFetchKey) return;
      this.notesFetchKey = key;
      try {
        await Promise.all([
          this.fetchNoteSummaries({
            periodicity: this.filters.periodicity,
            baseDate: this.filters.baseDate,
            flag: 'FULL',
          }),
          this.fetchNoteSummaries({
            periodicity: this.filters.periodicity,
            baseDate: this.filters.baseDate,
            flag: 'LTM',
          }),
        ]);
      } catch (e) {
        // Notes are additive UX; keep the report usable if summaries fail.
        console.warn(e);
      }
    },
    async reloadIfNeeded(force = false) {
      const key = `${this.filters.periodicity}|${this.filters.endDate || ''}|${this.filters.baseDate || ''}`;
      if (!force && key === this.fetchKey) return;
      this.fetchKey = key;
      try {
        await this.fetchReport({
          periodicity: this.filters.periodicity,
          endDate: this.filters.endDate || undefined,
          baseDate: this.filters.baseDate,
        });
        const fromReport = this.filters.endDate
          || this.report?.endDate
          || this.report?.maxEndDate
          || '';
        const saturday = snapToSaturdayIso(fromReport);
        if (saturday && saturday !== this.filters.endDate) {
          const needsRefetch = !!fromReport && saturday !== fromReport;
          this.filters.endDate = saturday;
          this.fetchKey = `${this.filters.periodicity}|${saturday}|${this.filters.baseDate || ''}`;
          if (needsRefetch) {
            await this.fetchReport({
              periodicity: this.filters.periodicity,
              endDate: saturday,
              baseDate: this.filters.baseDate,
            });
          }
        }
        this.appliedBaseDate = this.filters.baseDate;
        this.appliedPeriodicity = this.filters.periodicity;
        await this.reloadNotesIfNeeded(true);
      } catch (e) {
        // Keep the previous dataset on screen; error banner comes from Vuex.
      }
    },
  },
};
</script>
